import React, { useState, useEffect } from "react";
import { FaTrash, FaSave, FaEdit } from "react-icons/fa";
import axios from "axios";

const ChefInventory = () => {
    const [chefUsage, setChefUsage] = useState([]);
    const [editModes, setEditModes] = useState([]);
    const [editPopupOpen, setEditPopupOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        chefName: "",
        itemName: "",
        consume: 0,
        date: "",
    });
    const [selectedChefId, setSelectedChefId] = useState(null);

    // Fetch chef usage data
    useEffect(() => {
        const fetchChefUsage = async () => {
            const vendorId = localStorage.getItem("vendorId");
            const authToken = localStorage.getItem("authToken");

            if (!vendorId || !authToken) return;

            try {
                const response = await axios.get(
                    `https://backend.maamaas.com/food/api/inventory/chef/vendor/${vendorId}`,
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );

                const formatted = response.data.map((item) => ({
                    id: item.id,
                    chefName: item.chefName || "",
                    itemName: item.itemName || "",
                    consume: item.consume || 0,
                    date: item.date?.split("T")[0] || new Date().toISOString().split("T")[0],
                }));

                setChefUsage(formatted);
                setEditModes(formatted.map(() => false));
            } catch (err) {
                console.error(err);
                alert("Failed to fetch chef usage data.");
            }
        };

        fetchChefUsage();
    }, []);

    // Add new row
    const handleAddChefUsage = () => {
        const newRow = {
            id: null,
            chefName: "",
            itemName: "",
            consume: 0,
            date: new Date().toISOString().split("T")[0],
        };
        setChefUsage([...chefUsage, newRow]);
        setEditModes([...editModes.map(() => false), true]); // only new row editable
    };

    const handleChefChange = (e, index) => {
        const { name, value } = e.target;
        const updated = [...chefUsage];
        updated[index][name] = value;
        setChefUsage(updated);
    };

    const handleSubmitUsage = async (index) => {
        const vendorId = localStorage.getItem("vendorId");
        const authToken = localStorage.getItem("authToken");
        const row = chefUsage[index];

        if (!row.chefName || !row.itemName) {
            alert("Chef Name and Item Name are required.");
            return;
        }

        const payload = {
            chefName: row.chefName,
            itemName: row.itemName,
            consume: parseFloat(row.consume) || 0,
            date: row.date,
            vendorId: parseInt(vendorId),
        };

        try {
            if (row.id) {
                // existing row -> PUT
                await axios.put(
                    `https://backend.maamaas.com/food/api/inventory/chef/update/${row.id}`,
                    payload,
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
                alert("✅ Updated successfully!");
            } else {
                // new row -> POST
                const res = await axios.post(
                    `https://backend.maamaas.com/food/api/inventory/consume/${vendorId}`,
                    payload,
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
                const updated = [...chefUsage];
                updated[index].id = res.data.id; // set ID from backend
                setChefUsage(updated);
                alert("✅ Added successfully!");
            }

            const updatedEditModes = [...editModes];
            updatedEditModes[index] = false;
            setEditModes(updatedEditModes);
        } catch (err) {
            console.error(err);
            alert("Failed to submit usage.");
        }
    };

    const handleDeleteRow = async (index, id) => {
        if (!id) {
            const updated = chefUsage.filter((_, i) => i !== index);
            setChefUsage(updated);
            setEditModes(editModes.filter((_, i) => i !== index));
            return;
        }

        if (!window.confirm("Are you sure you want to delete this row?")) return;

        const authToken = localStorage.getItem("authToken");
        try {
            await axios.delete(
                `https://backend.maamaas.com/food/api/inventory/chef/delete/${id}`,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );

            const updated = chefUsage.filter((_, i) => i !== index);
            setChefUsage(updated);
            setEditModes(editModes.filter((_, i) => i !== index));
            alert("✅ Deleted successfully!");
        } catch (err) {
            console.error(err);
            alert("❌ Delete failed.");
        }
    };

    const handleEditPopupOpen = (row) => {
        if (!row.id) return alert("Cannot edit unsaved row. Use save button.");
        setSelectedChefId(row.id);
        setEditForm({ ...row });
        setEditPopupOpen(true);
    };

    const handleEditSubmit = async () => {
        const authToken = localStorage.getItem("authToken");
        try {
            await axios.put(
                `https://backend.maamaas.com/food/api/inventory/chef/update/${selectedChefId}`,
                editForm,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );

            const updated = chefUsage.map((row) =>
                row.id === selectedChefId ? { ...row, ...editForm } : row
            );
            setChefUsage(updated);
            setEditPopupOpen(false);
            alert("✅ Data updated successfully!");
        } catch (err) {
            console.error(err);
            alert("❌ Failed to update.");
        }
    };

    return (
        <div style={{ overflowY: "auto", height: "550px", marginTop: "-20px" }} className="invetory-data">
            <button onClick={handleAddChefUsage} style={addButtonStyle}>
                Add +
            </button>

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>S.No</th>
                        <th style={tableHeaderStyle}>Chef Name</th>
                        <th style={tableHeaderStyle}>Item Name</th>
                        <th style={tableHeaderStyle}>Consumed</th>
                        <th style={tableHeaderStyle}>Date</th>
                        <th style={tableHeaderStyle}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {chefUsage.map((row, index) => (
                        <tr key={row.id || index} style={index % 2 === 0 ? evenRowStyle : oddRowStyle}>
                            <td style={tableCellStyle}>{index + 1}</td>
                            <td style={tableCellStyle}>
                                <input
                                    name="chefName"
                                    value={row.chefName}
                                    onChange={(e) => handleChefChange(e, index)}
                                    style={inputStyle}
                                    readOnly={!editModes[index]}
                                />
                            </td>
                            <td style={tableCellStyle}>
                                <input
                                    name="itemName"
                                    value={row.itemName}
                                    onChange={(e) => handleChefChange(e, index)}
                                    style={inputStyle}
                                    readOnly={!editModes[index]}
                                />
                            </td>
                            <td style={tableCellStyle}>
                                <input
                                    name="consume"
                                    type="number"
                                    value={row.consume}
                                    onChange={(e) => handleChefChange(e, index)}
                                    style={inputStyle}
                                    readOnly={!editModes[index]}
                                />
                            </td>
                            <td style={tableCellStyle}>
                                <input
                                    name="date"
                                    type="date"
                                    value={row.date}
                                    onChange={(e) => handleChefChange(e, index)}
                                    style={inputStyle}
                                    readOnly={!editModes[index]}
                                />
                            </td>
                            <td style={tableCellStyle}>
                                {editModes[index] ? (
                                    <button onClick={() => handleSubmitUsage(index)} style={{ ...editButtonStyle, backgroundColor: "green" }}>
                                        <FaSave />
                                    </button>
                                ) : (
                                    <button onClick={() => handleEditPopupOpen(row)} style={{ ...editButtonStyle, backgroundColor: "#007bff" }}>
                                        <FaEdit />
                                    </button>
                                )}
                                <button onClick={() => handleDeleteRow(index, row.id)} style={deleteButtonStyle}>
                                    <FaTrash />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Edit popup */}
            {editPopupOpen && (
                <div style={popupOverlayStyle}>
                    <div style={popupContentStyle}>
                        <h3>Edit Chef Data</h3>
                        <input
                            type="text"
                            value={editForm.chefName}
                            onChange={(e) => setEditForm({ ...editForm, chefName: e.target.value })}
                            style={popupInputStyle}
                        />
                        <input
                            type="text"
                            value={editForm.itemName}
                            onChange={(e) => setEditForm({ ...editForm, itemName: e.target.value })}
                            style={popupInputStyle}
                        />
                        <input
                            type="number"
                            value={editForm.consume}
                            onChange={(e) => setEditForm({ ...editForm, consume: e.target.value })}
                            style={popupInputStyle}
                        />
                        <input
                            type="date"
                            value={editForm.date}
                            onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                            style={popupInputStyle}
                        />
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "15px" }}>
                            <button onClick={handleEditSubmit} style={saveButtonStyle}>Save</button>
                            <button onClick={() => setEditPopupOpen(false)} style={deleteButtonStyle}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Styles
const tableHeaderStyle = { padding: "12px", border: "1px solid #ddd", textAlign: "center", fontWeight: "bold", backgroundColor: "#b15cd6", color: "white" };
const tableCellStyle = { padding: "12px", border: "1px solid #ddd", textAlign: "center" };
const evenRowStyle = {};
const oddRowStyle = { backgroundColor: "#fff" };
const inputStyle = { width: "100%", border: "none", textAlign: "center", backgroundColor: "transparent" };
const addButtonStyle = { marginTop: "20px", padding: "8px 16px", backgroundColor: "#b15cd6", color: "white", border: "none", cursor: "pointer", borderRadius: "5px", width: "100px" };
const editButtonStyle = { color: "white", border: "none", padding: "5px 10px", cursor: "pointer", borderRadius: "5px", marginRight: "5px" };
const deleteButtonStyle = { backgroundColor: "red", color: "white", border: "none", padding: "5px 10px", cursor: "pointer", borderRadius: "5px" };
const popupOverlayStyle = { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const popupContentStyle = { backgroundColor: "#fff", borderRadius: "12px", padding: "30px", width: "400px", boxShadow: "0 4px 12px rgba(0,0,0,0.2)", fontFamily: "Arial, sans-serif" };
const popupInputStyle = { width: "100%", padding: "10px", marginBottom: "12px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "14px" };
const saveButtonStyle = { backgroundColor: "#4CAF50", color: "white", padding: "10px 20px", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" };

export default ChefInventory;
